import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Plus, Pencil, X } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/vacant-funkce")({ component: VakantFeaturesAdminPage });

type Row = {
  id: string;
  title: string;
  description: string | null;
  url: string | null;
  kind: "info" | "feature";
  active: boolean;
  sort_order: number;
  created_at: string;
};

type FormState = { title: string; description: string; url: string; kind: "info" | "feature"; active: boolean; sort_order: number };
const empty: FormState = { title: "", description: "", url: "", kind: "feature", active: true, sort_order: 0 };

function VakantFeaturesAdminPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(empty);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "vakant-features"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vakant_owner_features")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const reset = () => { setForm(empty); setEditingId(null); };

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        url: form.url.trim() || null,
        kind: form.kind,
        active: form.active,
        sort_order: Number(form.sort_order) || 0,
      };
      if (!payload.title) throw new Error("Název je povinný.");
      if (editingId) {
        const { error } = await supabase.from("vakant_owner_features").update(payload).eq("id", editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("vakant_owner_features").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(editingId ? "Uloženo." : "Přidáno.");
      reset();
      qc.invalidateQueries({ queryKey: ["admin", "vakant-features"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleMut = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("vakant_owner_features").update({ active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "vakant-features"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("vakant_owner_features").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Smazáno.");
      qc.invalidateQueries({ queryKey: ["admin", "vakant-features"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const startEdit = (r: Row) => {
    setEditingId(r.id);
    setForm({
      title: r.title,
      description: r.description ?? "",
      url: r.url ?? "",
      kind: r.kind,
      active: r.active,
      sort_order: r.sort_order,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl">Funkce a informace — Majitelé Vacantů</h1>
        <p className="text-sm text-muted-foreground">
          Vkládejte funkce a informace, které uvidí uživatelé s rolí „Majitel Vacantů" na svém dashboardu.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <h2 className="font-medium">{editingId ? "Upravit položku" : "Přidat položku"}</h2>
            {editingId && (
              <Button variant="ghost" size="sm" onClick={reset}><X className="h-4 w-4" /> Zrušit úpravu</Button>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Název *</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="např. Stáhnout manuál Vacantů" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Popis</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Odkaz (URL)</label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Typ</label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v as "info" | "feature" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature">Funkce</SelectItem>
                  <SelectItem value="info">Informace</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Pořadí</label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-sm">Aktivní</span>
            </div>
          </div>
          <div>
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} className="gap-1.5">
              {editingId ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {editingId ? "Uložit změny" : "Přidat"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pořadí</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Název</TableHead>
                <TableHead>Odkaz</TableHead>
                <TableHead>Aktivní</TableHead>
                <TableHead className="text-right">Akce</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">Načítám…</TableCell></TableRow>}
              {!isLoading && rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">Zatím žádné položky.</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.sort_order}</TableCell>
                  <TableCell>
                    <Badge variant={r.kind === "feature" ? "default" : "secondary"}>
                      {r.kind === "feature" ? "Funkce" : "Informace"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{r.title}</div>
                    {r.description && <div className="text-xs text-muted-foreground line-clamp-2">{r.description}</div>}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{r.url}</a> : "—"}
                  </TableCell>
                  <TableCell>
                    <Switch checked={r.active} onCheckedChange={(v) => toggleMut.mutate({ id: r.id, active: v })} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(r)} aria-label="Upravit"><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => delMut.mutate(r.id)} aria-label="Smazat"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
